import { logEvent } from '@/util/analytics'
import { client } from '@/util/hono'
import { Dialog } from '@beeirl/ui/dialog'
import { Form } from '@beeirl/ui/form'
import { LineIcon } from '@beeirl/ui/line-icon'
import { cn } from '@beeirl/ui/utils/cn'
import { useStore } from '@tanstack/react-store'
import React from 'react'
import { z } from 'zod/v4'
import { toastManager } from './toast'

export const SupportDialog = Dialog.Root

export const SupportDialogTrigger = Dialog.Trigger

export function SupportDialogPopup({
  defaultCategory = 'problem',
  location,
}: {
  defaultCategory?: 'problem' | 'question' | 'feedback'
  location: string
}) {
  const { open, setOpen } = Dialog.useContext()

  React.useEffect(() => {
    if (open) {
      logEvent('support_dialog_opened', { location })
    }
  }, [open])

  const form = Form.use({
    defaultValues: {
      category: defaultCategory,
      email: '',
      name: '',
      message: '',
    },
    validators: {
      onSubmit: z.object({
        category: z.enum(['problem', 'question', 'feedback']),
        email: z.email('Invalid email address').or(z.literal('')),
        name: z.string().or(z.literal('')),
        message: z.string().min(1, 'Please write a message before sending'),
      }),
    },
    onSubmit: async ({ formApi, value }) => {
      client.support.$post({
        json: {
          ...value,
          location,
        },
      })
      logEvent('support_message_sent', { location })
      formApi.reset()
      setOpen(false)
      toastManager.add({
        title: 'Message sent',
        description: 'Message was sent to the Postfully team!',
      })
    },
  })
  const formStore = useStore(form.store, (state) => ({
    category: state.values.category,
  }))

  return (
    <Dialog.Popup className="max-w-lg">
      <Dialog.Header>
        <Dialog.Title>
          {
            {
              problem: 'Contact us',
              question: 'Ask a question',
              feedback: 'Send feedback',
            }[formStore.category]
          }
        </Dialog.Title>
        <Dialog.Description>
          {
            {
              problem:
                'What is the issue? If you’re reporting a bug, what are the steps you took so we can reproduce the behaviour?',
              question:
                'How can we help? Please share any relevant information we may need to answer your question.',
              feedback:
                'How can we improve Postfully? If you have a feature request, can you also share how you would use it and why it’s important to you?',
            }[formStore.category]
          }
        </Dialog.Description>
      </Dialog.Header>
      <form
        className="mt-4 space-y-4"
        onSubmit={(e) => {
          e.preventDefault()
          e.stopPropagation()
          form.handleSubmit()
        }}
      >
        <form.Field name="category">
          {(field) => (
            <div className="flex rounded-lg border border-gray-200 p-1 max-sm:flex-col">
              <button
                className={cn(
                  'flex w-full cursor-pointer items-center gap-2 rounded-md px-2.5 py-1.5 text-sm font-medium sm:justify-center',
                  field.state.value === 'problem' && 'bg-gray-100'
                )}
                type="button"
                onClick={() => field.setValue('problem')}
              >
                <LineIcon.AlertTriangle className="size-4" />
                Problem
              </button>
              <button
                className={cn(
                  'flex w-full cursor-pointer items-center gap-2 rounded-md px-2 py-1.5 text-sm font-medium sm:justify-center',
                  field.state.value === 'question' && 'bg-gray-100'
                )}
                type="button"
                onClick={() => field.setValue('question')}
              >
                <LineIcon.Question className="size-4" />
                Question
              </button>
              <button
                className={cn(
                  'flex w-full cursor-pointer items-center gap-2 rounded-md px-2 py-1.5 text-sm font-medium sm:justify-center',
                  field.state.value === 'feedback' && 'bg-gray-100'
                )}
                type="button"
                onClick={() => field.setValue('feedback')}
              >
                <LineIcon.Bulb className="size-4" />
                Feedback
              </button>
            </div>
          )}
        </form.Field>
        <form.AppField name="name">
          {(field) => (
            <field.Root>
              <field.Label>
                Name <span className="text-gray-500">(optional)</span>
              </field.Label>
              <field.Control render={<field.TextInput />} />
            </field.Root>
          )}
        </form.AppField>
        <form.AppField name="email">
          {(field) => (
            <field.Root>
              <field.Label>
                Email <span className="text-gray-500">(optional)</span>
              </field.Label>
              <field.Control render={<field.TextInput />} />
            </field.Root>
          )}
        </form.AppField>
        <form.AppField name="message">
          {(field) => (
            <field.Root>
              <field.Label>Message</field.Label>
              <field.Control
                render={
                  <field.TextArea
                    autoFocus
                    placeholder={
                      {
                        problem: 'Something seems wrong...',
                        question: 'How do I...',
                        feedback: 'What if...',
                      }[formStore.category]
                    }
                    rows={4}
                  />
                }
              />
            </field.Root>
          )}
        </form.AppField>
        <form.AppForm>
          <form.SubmitButton className="w-full" size="lg">
            {
              {
                problem: 'Send message',
                question: 'Send question',
                feedback: 'Send feedback',
              }[formStore.category]
            }
          </form.SubmitButton>
        </form.AppForm>
      </form>
      <Dialog.Footer className="-mb-2 mt-3 text-center">
        <span className="text-sm text-gray-500">
          You can also email us at{' '}
          <a
            className="text-gray-900"
            href={`mailto:mail@postfully.app?subject=${
              {
                problem: 'Problem',
                question: 'Question',
                feedback: 'Feedback',
              }[formStore.category]
            }`}
          >
            mail@postfully.app
          </a>
          .
        </span>
      </Dialog.Footer>
    </Dialog.Popup>
  )
}
